/**
 * Owner: Masa Bayu
 * File: qxw-qxp-mapping-registry.ts
 * Date:Time: 2026-09-20 23:37
 * Description: B1 exhaustive QXW→QXPPackage v1 mapping registry · no emit.
 */
import { QX_AUTHORING_CONCEPT_MAPPINGS } from './text-authoring-mapping-registry';
import { QX_FIGURE_ASSET_KINDS } from './text-figure';
import { QX_TEXT_MARK_TYPES } from './text-mark';
/**
 * Locked QXP package target for B1 — mirrors @qx/qxp-core QXP_VERSION.
 * Compatibility asserted in tests against qxp-core vocabulary.
 */
export const QXW_QXP_TARGET_VERSION = '1.0.0';
/** Mirrored QXP v1.0.0 kind vocabularies (qxp-codes.ts) — do not invent kinds. */
export const QXW_QXP_V1_KIND_VOCAB = {
    content: ['document', 'chapter', 'section', 'text', 'table', 'other'],
    structure: ['root', 'chapter', 'section', 'heading', 'other'],
    layout: ['page', 'spread', 'region', 'other'],
    asset: ['image', 'audio', 'video', 'font', 'file', 'other'],
    navigation: ['toc', 'landmark', 'link', 'spine', 'other'],
    accessibility: ['alt-text', 'aria-label', 'reading-order', 'caption', 'other'],
};
/**
 * Exhaustive CA-02…09 publication concepts → QXPPackage v1.0.0 planes/kinds.
 * Unlisted → REJECT. Forbidden concepts listed with status invalid.
 */
export const QXW_QXP_CONCEPT_MAPPINGS = [
    {
        conceptId: 'text-content',
        caGate: 'CA-02',
        status: 'supported',
        qxpPlane: 'content',
        qxpKind: 'text',
        qxpTarget: 'QXPContentRef { kind: "text", targetRef → text element id }',
    },
    {
        conceptId: 'paragraph',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Embedded in content resource for QXPContentRef(text) · not a separate package field',
    },
    {
        conceptId: 'heading',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'structure',
        qxpKind: 'heading',
        qxpTarget: 'QXPStructureRef { kind: "heading" } + content resource inlines',
    },
    {
        conceptId: 'quote',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Embedded in content resource for QXPContentRef(text)',
    },
    {
        conceptId: 'code',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Embedded in content resource for QXPContentRef(text)',
    },
    {
        conceptId: 'list',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Embedded in content resource for QXPContentRef(text)',
    },
    {
        conceptId: 'list-item',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Embedded under list in content resource',
    },
    {
        conceptId: 'text',
        caGate: 'CA-03',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Inline text payload in content resource',
    },
    {
        conceptId: 'mark',
        caGate: 'CA-05',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: 'text',
        qxpTarget: 'Marks travel inside content resource inlines · not QXPPackage fields',
    },
    {
        conceptId: 'link',
        caGate: 'CA-05',
        status: 'supported',
        qxpPlane: 'navigation',
        qxpKind: 'link',
        qxpTarget: 'QXPNavigationRef { kind: "link" } + inline link in content resource',
    },
    {
        conceptId: 'figure',
        caGate: 'CA-06',
        status: 'supported',
        qxpPlane: 'content',
        qxpKind: 'other',
        qxpTarget: 'QXPContentRef { kind: "other", targetRef → figure identity } + asset/a11y refs',
    },
    {
        conceptId: 'assetRef',
        caGate: 'CA-06',
        status: 'supported',
        qxpPlane: 'asset',
        qxpKind: 'image',
        qxpTarget: 'QXPAssetRef { kind from figure assetKind · targetRef → QXAsset.id } · no binary embed',
    },
    {
        conceptId: 'caption',
        caGate: 'CA-06',
        status: 'supported',
        qxpPlane: 'accessibility',
        qxpKind: 'caption',
        qxpTarget: 'QXPAccessibilityRef { kind: "caption" } · caption ≠ alt',
    },
    {
        conceptId: 'alt',
        caGate: 'CA-06 / CA-08',
        status: 'supported',
        qxpPlane: 'accessibility',
        qxpKind: 'alt-text',
        qxpTarget: 'QXPAccessibilityRef { kind: "alt-text" }',
    },
    {
        conceptId: 'language',
        caGate: 'CA-07',
        status: 'supported',
        qxpPlane: 'identity',
        qxpKind: 'languageRef',
        qxpTarget: 'QXPPublicationIdentity.languageRef (+ block language in content resource)',
    },
    {
        conceptId: 'footnote',
        caGate: 'CA-03 / CA-08',
        status: 'supported',
        qxpPlane: 'content',
        qxpKind: 'other',
        qxpTarget: 'QXPContentRef { kind: "other", targetRef → footnote.id }',
    },
    {
        conceptId: 'endnote',
        caGate: 'CA-03 / CA-08',
        status: 'supported',
        qxpPlane: 'content',
        qxpKind: 'other',
        qxpTarget: 'QXPContentRef { kind: "other", targetRef → endnote.id }',
    },
    {
        conceptId: 'note-ref',
        caGate: 'CA-03 / CA-08',
        status: 'supported',
        qxpPlane: 'navigation',
        qxpKind: 'link',
        qxpTarget: 'QXPNavigationRef { kind: "link", targetRef → note id }',
    },
    {
        conceptId: 'reading-order',
        caGate: 'CA-08',
        status: 'supported',
        qxpPlane: 'accessibility',
        qxpKind: 'reading-order',
        qxpTarget: 'QXPAccessibilityRef { kind: "reading-order" } · derived · not a QXW store field',
    },
    {
        conceptId: 'derived-role',
        caGate: 'CA-08',
        status: 'supported',
        qxpPlane: 'embedded',
        qxpKind: null,
        qxpTarget: 'Derived at compile from semantic type · not stored on QXPPackage as aria bag',
    },
    {
        conceptId: 'contentReference',
        caGate: 'CA-10 / PSA',
        status: 'supported',
        qxpPlane: 'content',
        qxpKind: 'document',
        qxpTarget: 'PSA contentElementIds → QXPContentRef targets (publication binding)',
    },
    {
        conceptId: 'publication-identity',
        caGate: 'CA-10 / PSA-07',
        status: 'supported',
        qxpPlane: 'identity',
        qxpKind: 'publicationIdentity',
        qxpTarget: 'QXPPublicationIdentity · sourceDocumentRef → QXW document id',
    },
    {
        conceptId: 'structure-root',
        caGate: 'PSA-07 Q1',
        status: 'supported',
        qxpPlane: 'structure',
        qxpKind: 'root',
        qxpTarget: 'QXPStructureRef { kind: "root" }',
    },
    {
        conceptId: 'structure-chapter',
        caGate: 'PSA-07 Q2',
        status: 'supported',
        qxpPlane: 'structure',
        qxpKind: 'chapter',
        qxpTarget: 'QXPStructureRef { kind: "chapter" }',
    },
    {
        conceptId: 'structure-section',
        caGate: 'PSA-07 Q2',
        status: 'supported',
        qxpPlane: 'structure',
        qxpKind: 'section',
        qxpTarget: 'QXPStructureRef { kind: "section" }',
    },
    {
        conceptId: 'region-front-matter',
        caGate: 'PSA-07 Q5',
        status: 'supported',
        qxpPlane: 'layout',
        qxpKind: 'region',
        qxpTarget: 'QXPLayoutRef { kind: "region" } · front-matter',
    },
    {
        conceptId: 'region-main-content',
        caGate: 'PSA-07 Q5',
        status: 'supported',
        qxpPlane: 'layout',
        qxpKind: 'region',
        qxpTarget: 'QXPLayoutRef { kind: "region" } · main-content',
    },
    {
        conceptId: 'region-back-matter',
        caGate: 'PSA-07 Q5',
        status: 'supported',
        qxpPlane: 'layout',
        qxpKind: 'region',
        qxpTarget: 'QXPLayoutRef { kind: "region" } · back-matter',
    },
    {
        conceptId: 'stable-node-id',
        caGate: 'PSA-07 Q6',
        status: 'supported',
        qxpPlane: 'navigation',
        qxpKind: 'landmark',
        qxpTarget: 'QXW node id preserved as QXP targetRef / nav identity',
    },
    {
        conceptId: 'dom',
        caGate: 'CA-10 P1',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — DOM is not a QXP input authority',
    },
    {
        conceptId: 'css',
        caGate: 'CA-10 / QXP leakage',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — CSS not a QXPPackage field',
    },
    {
        conceptId: 'binary-bag',
        caGate: 'CA-06 / CA-10',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — binaries not embedded in QXW or QXPPackage',
    },
    {
        conceptId: 'aria-bag',
        caGate: 'CA-08',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — no free-form aria-* bags on QXW or QXP',
    },
    {
        conceptId: 'epub',
        caGate: 'CA-10 / QX-DOC',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — EPUB ↛ QXP source of truth',
    },
    {
        conceptId: 'editor-ui-state',
        caGate: 'Phase 12',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — Editor UI state is not publication input',
    },
    {
        conceptId: 'adam-proposal',
        caGate: 'Phase 11 / CA-10',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — ADAM proposals are not QXP inputs (accept → QXW first)',
    },
    {
        conceptId: 'qxp-as-sot',
        caGate: 'CA-09 Q6',
        status: 'invalid',
        qxpPlane: null,
        qxpKind: null,
        qxpTarget: 'FORBIDDEN — QXP is not authoring SoT',
    },
];
const BY_ID = new Map(QXW_QXP_CONCEPT_MAPPINGS.map((entry) => [entry.conceptId, entry]));
export const QXW_QXP_SUPPORTED_MARK_TYPES = QX_TEXT_MARK_TYPES;
export const QXW_QXP_FIGURE_ASSET_KIND_MAP = {
    image: 'image',
    svg: 'image',
};
export function getQXWQXPConceptMapping(conceptId) {
    return BY_ID.get(conceptId);
}
export function isQXWQXPConceptRegistered(conceptId) {
    return BY_ID.has(conceptId);
}
export function listRequiredAuthoringConceptIdsForQXP() {
    return QX_AUTHORING_CONCEPT_MAPPINGS.map((entry) => entry.conceptId);
}
//# sourceMappingURL=qxw-qxp-mapping-registry.js.map